import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field } from "@/components/shared/Common";
import { api, errMsg } from "@/lib/api";
import { Pick } from "@/pages/gerencia/Sales";
import { ItemPicker, pickedItems } from "./ItemPicker";

const TITLES = { ingreso: "Ingreso de stock", transferencia: "Transferir a otra bodega", entrega: "Entregar a OT", devolucion: "Devolución desde OT" };

const useAllProducts = (open) => {
  const [list, setList] = useState([]);
  useEffect(() => { if (open) api.get("/abastecimiento/products", { params: { page_size: 500 } }).then((r) => setList(r.data.items.map((p) => ({ product_id: p.id, sku: p.sku, name: p.name, unit: p.unit, unit_value: p.unit_value })))); }, [open]);
  return list;
};

export const MoveDialog = ({ kind, wh, onClose, onDone }) => {
  const open = !!kind;
  const all = useAllProducts(open && ["ingreso", "devolucion"].includes(kind));
  const [picked, setPicked] = useState({});
  const [f, setF] = useState({});
  const [lk, setLk] = useState({ ots: [], whs: [], workers: [] });
  useEffect(() => {
    if (!open) return;
    setPicked({}); setF({});
    if (kind === "transferencia") api.get("/abastecimiento/warehouses/all").then((r) => setLk((l) => ({ ...l, whs: r.data.filter((w) => w.id !== wh.id) })));
    if (["entrega", "devolucion"].includes(kind)) api.get("/abastecimiento/ots").then((r) => setLk((l) => ({ ...l, ots: r.data })));
  }, [open, kind, wh]);
  useEffect(() => { if (f.ot_id) api.get(`/abastecimiento/ots/${f.ot_id}/workers`).then((r) => setLk((l) => ({ ...l, workers: r.data }))); }, [f.ot_id]);
  const fromStock = ["transferencia", "entrega"].includes(kind);
  const options = fromStock ? (wh?.stock || []).filter((s) => s.qty > 0) : all;
  const submit = async () => {
    const items = pickedItems(picked);
    if (!items.length) return toast.error("Seleccione al menos un producto");
    if (items.some((i) => i.qty <= 0)) return toast.error("Indique la cantidad de cada producto seleccionado");
    try {
      let r;
      if (kind === "ingreso") r = await api.post("/abastecimiento/receipts", { warehouse_id: wh.id, reference: f.reference || "", note: f.note || "", items: items.map((i) => (f.values?.[i.product_id] !== undefined && f.values[i.product_id] !== "" ? { ...i, unit_value: parseInt(f.values[i.product_id], 10) } : i)) });
      if (kind === "transferencia") r = await api.post("/abastecimiento/transfers", { from_id: wh.id, to_id: f.to_id, note: f.note || "", items });
      if (kind === "entrega") r = await api.post("/abastecimiento/deliveries", { warehouse_id: wh.id, ot_id: f.ot_id, employee_id: f.employee_id || null, note: f.note || "", items });
      if (kind === "devolucion") r = await api.post("/abastecimiento/returns", { warehouse_id: wh.id, ot_id: f.ot_id, employee_id: f.employee_id || null, note: f.note || "", items });
      toast.success(`${r.data.code} registrado`); onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[94vh] overflow-y-auto" data-testid={`move-dialog-${kind}`}>
        <DialogHeader><DialogTitle>{TITLES[kind]} · {wh?.code}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          {kind === "ingreso" && <div className="sm:col-span-2"><Field label="Referencia * (factura, guía, etc.)"><Input data-testid="move-reference-input" value={f.reference || ""} onChange={(e) => setF({ ...f, reference: e.target.value })} /></Field></div>}
          {kind === "transferencia" && <div className="sm:col-span-2"><Field label="Bodega destino *"><Pick testid="move-to-select" value={f.to_id} onChange={(v) => setF({ ...f, to_id: v })} options={lk.whs} placeholder="Seleccione" /></Field></div>}
          {["entrega", "devolucion"].includes(kind) && <>
            <Field label="OT *"><Pick testid="move-ot-select" value={f.ot_id} onChange={(v) => setF({ ...f, ot_id: v, employee_id: null })} options={lk.ots} placeholder="Seleccione" /></Field>
            <Field label={kind === "entrega" ? "Recibe (trabajador)" : "Devuelve (trabajador)"}><Pick testid="move-employee-select" value={f.employee_id} onChange={(v) => setF({ ...f, employee_id: v })} options={lk.workers} placeholder="Opcional" /></Field>
          </>}
        </div>
        <ItemPicker options={options} value={picked} onChange={setPicked} showStock={fromStock} />
        {kind === "ingreso" && Object.keys(picked).length > 0 && all[0]?.unit_value !== undefined && <div className="space-y-1.5" data-testid="move-values">
          <div className="text-[13px] font-semibold">Valor unitario (opcional, actualiza el valor del producto)</div>
          {Object.keys(picked).map((pid) => { const p = all.find((x) => x.product_id === pid); return (
            <div key={pid} className="flex items-center gap-2 text-sm"><span className="flex-1 truncate">{p?.name}</span>
              <Input data-testid={`move-value-${pid}`} type="number" min="0" className="w-32 h-8" placeholder={String(p?.unit_value ?? "")} value={f.values?.[pid] ?? ""} onChange={(e) => setF({ ...f, values: { ...(f.values || {}), [pid]: e.target.value } })} /></div>); })}
        </div>}
        <Field label="Observación"><Textarea data-testid="move-note-input" rows={2} value={f.note || ""} onChange={(e) => setF({ ...f, note: e.target.value })} /></Field>
        <DialogFooter><Button data-testid="move-submit-btn" className="rounded-full" onClick={submit}>Confirmar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const AdjustDialog = ({ row, wh, onClose, onDone }) => {
  const [f, setF] = useState({ new_qty: "", reason: "" });
  useEffect(() => { if (row) setF({ new_qty: String(row.qty), reason: "" }); }, [row]);
  const save = async () => {
    try { await api.post("/abastecimiento/adjustments", { warehouse_id: wh.id, product_id: row.product_id, new_qty: parseFloat(f.new_qty), reason: f.reason }); toast.success("Ajuste registrado"); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="adjust-dialog">
        <DialogHeader><DialogTitle>Ajuste de inventario · {row?.name}</DialogTitle></DialogHeader>
        <p className="text-sm text-[#5b6656]">Stock actual: <b>{row?.qty} {row?.unit}</b></p>
        <Field label="Stock contado *"><Input data-testid="adjust-qty-input" type="number" min="0" step="any" value={f.new_qty} onChange={(e) => setF({ ...f, new_qty: e.target.value })} /></Field>
        <Field label="Motivo *"><Textarea data-testid="adjust-reason-input" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} /></Field>
        <DialogFooter><Button data-testid="adjust-save-btn" className="rounded-full" onClick={save}>Registrar ajuste</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const MinDialog = ({ row, wh, onClose, onDone }) => {
  const [v, setV] = useState("");
  useEffect(() => { if (row) setV(row.min_override ?? ""); }, [row]);
  const save = async () => {
    try { await api.put(`/abastecimiento/warehouses/${wh.id}/stock/${row.product_id}/min`, { min: v === "" ? null : parseFloat(v) }); toast.success("Mínimo guardado"); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="min-dialog">
        <DialogHeader><DialogTitle>Stock mínimo en {wh?.code} · {row?.name}</DialogTitle></DialogHeader>
        <Field label="Mínimo para esta bodega" hint={wh?.type === "central" ? "Vacío = usa el mínimo del producto." : "Vacío = sin alerta en esta sub-bodega."}>
          <Input data-testid="min-input" type="number" min="0" step="any" value={v} onChange={(e) => setV(e.target.value)} /></Field>
        <p className="text-xs text-[#5b6656]">Se avisa cuando el stock llega a 125% del mínimo (cerca) y cuando queda en o bajo el mínimo.</p>
        <DialogFooter><Button data-testid="min-save-btn" className="rounded-full" onClick={save}>Guardar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
